import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Award, Star } from 'lucide-react'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { FilterBar, SearchInput } from '@/shared/ui/filters'
import { EmptyState, ErrorStrip, Forbidden, LoadingRows } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { usePoliticsCodes, CODE_LIST_UNAVAILABLE } from '@/features/polling/useVocabulary'
import { listCandidates, listCoalitions, listParties, listSeatTypes } from './api'
import { CandidateDrawer } from './CandidateDrawer'
import { CandidateFormDialog } from './CandidateFormDialog'
import {
  EntityChip, Pill, candidateStatusLabel, candidateStatusTone,
  seatTypeLabel,
} from './vocab'

const PAGE_SIZE = 50
const LOOKUP_PAGE = 200

/**
 * Who is standing, for what, and where.
 *
 * ## Five filters, all of them the server's
 *
 * `search`, `seatType`, `status`, `partyId`, `coalitionId` are query parameters.
 * The seat-type options come from `listSeatTypes()`, and the party and coalition
 * options come from the registers themselves — nothing here is a hand-written
 * option list. PollSphere's candidate filters offered `STATION`, `TRACKING`,
 * `YOUTH` and `RURAL`, matched nothing, and said nothing about it; that is the
 * defect this whole port exists to remove.
 *
 * ## The unit column carries its ancestors
 *
 * "Kileleshwa" is ambiguous and "Kenya › Nairobi › Westlands › Kileleshwa" is not.
 * `unitLabels` arrives with the row, so the path costs no extra request and cannot
 * disagree with the drawer.
 */
export function CandidatesPage() {
  const canRead = useCan('politics:read')
  const canWrite = useCan('politics:candidate')

  const [params, setParams] = useSearchParams()
  // The code list is the server's; `vocab.tsx` only owns the English.
  const statuses = usePoliticsCodes('candidateStatus')
  const q = params.get('q') ?? ''
  const seatType = params.get('seatType') ?? ''
  const status = params.get('status') ?? ''
  const partyId = params.get('party') ?? ''
  const coalitionId = params.get('coalition') ?? ''
  const page = Number(params.get('page') ?? 0)
  const openId = params.get('candidate')

  const [creating, setCreating] = useState(false)

  const list = useLoad(() => listCandidates({
    search: q || undefined,
    seatType: seatType || undefined,
    status: status || undefined,
    partyId: partyId || undefined,
    coalitionId: coalitionId || undefined,
  }, page, PAGE_SIZE), [q, seatType, status, partyId, coalitionId, page])

  const seatTypes = useLoad(() => listSeatTypes(), [])
  const parties = useLoad(() => listParties({}, 0, LOOKUP_PAGE), [])
  const coalitions = useLoad(() => listCoalitions({}, 0, LOOKUP_PAGE), [])

  if (!canRead) return <Forbidden what="the candidate register" />

  function patch(next: Record<string, string>) {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      // Page zero is the default, so it is absent from the URL rather than
      // spelled out — a shared link should not carry `?page=0`.
      if (v && !(k === 'page' && v === '0')) sp.set(k, v)
      else sp.delete(k)
    }
    if (!('page' in next)) sp.delete('page')
    setParams(sp, { replace: true })
  }

  function clearFilters() {
    patch({ q: '', seatType: '', status: '', party: '', coalition: '' })
  }

  const rows = list.data?.content ?? []
  const activeCount = [q, seatType, status, partyId, coalitionId].filter(Boolean).length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Candidates"
        description="Every person standing, with the seat they contest and the banner they carry."
        right={canWrite && (
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            onClick={() => setCreating(true)}>
            New candidate
          </button>
        )}
      />

      <FilterBar activeCount={activeCount}>
        <SearchInput value={q} onChange={(v) => patch({ q: v })} placeholder="Name…" />

        <select className="erp-input axis-full-sm" aria-label="Seat type" value={seatType}
          onChange={(e) => patch({ seatType: e.target.value })}>
          {/* From /politics/seat-types. The one code list this module publishes,
              so it is the one filter here that can never drift. */}
          <option value="">Any seat</option>
          {(seatTypes.data ?? []).map((s) => (
            <option key={s.code} value={s.code}>{seatTypeLabel(s.code)}</option>
          ))}
        </select>

        <select className="erp-input axis-full-sm" aria-label="Status" value={status}
          onChange={(e) => patch({ status: e.target.value })}>
          <option value="">Any status</option>
          {statuses.codes.map((s) => (
            <option key={s} value={s}>{candidateStatusLabel(s)}</option>
          ))}
        </select>
        {statuses.unavailable && (
          <span className="text-erp-xs text-red-700">{CODE_LIST_UNAVAILABLE}</span>
        )}

        <select className="erp-input axis-full-sm" aria-label="Party" value={partyId}
          onChange={(e) => patch({ party: e.target.value })}>
          <option value="">Any party</option>
          {(parties.data?.content ?? []).map((p) => (
            <option key={p.id} value={p.id}>{p.shortName || p.name}</option>
          ))}
        </select>

        <select className="erp-input axis-full-sm" aria-label="Coalition" value={coalitionId}
          onChange={(e) => patch({ coalition: e.target.value })}>
          <option value="">Any coalition</option>
          {(coalitions.data?.content ?? []).map((c) => (
            <option key={c.id} value={c.id}>{c.shortName || c.name}</option>
          ))}
        </select>

        {activeCount > 0 && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={clearFilters}>
            Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
          </button>
        )}
      </FilterBar>

      <Panel bodyClassName="p-0">
        <ErrorStrip message={list.error} />
        <ErpTable head={(
          <>
            <Th>Name</Th>
            <Th>Seat</Th>
            <Th>Unit</Th>
            <Th>Party</Th>
            <Th className="axis-hide-sm">Coalition</Th>
            <Th>Status</Th>
          </>
        )}>
          {list.loading && rows.length === 0 ? (
            <LoadingRows colSpan={6} label="Loading candidates…" />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={6}>
                <EmptyState
                  icon={<Award className="h-4 w-4" />}
                  title={activeCount > 0 ? 'No candidate matches these filters' : 'No candidates yet'}
                  hint={activeCount > 0
                    ? 'Clear a filter to widen the search.'
                    : 'A candidate needs a seat and a unit at the level that seat contests. The '
                      + 'party is optional — somebody with none stands as an independent.'}
                  action={activeCount > 0
                    ? <button type="button" className="erp-btn" onClick={clearFilters}>Clear filters</button>
                    : canWrite
                      ? (
                        <button type="button" className="erp-btn erp-btn-primary"
                          onClick={() => setCreating(true)}>Add a candidate</button>
                      )
                      : undefined}
                />
              </td>
            </tr>
          ) : rows.map((c) => (
            <tr
              key={c.id}
              className="hover:bg-muted/40 cursor-pointer"
              tabIndex={0}
              onClick={() => patch({ candidate: c.id, page: String(page) })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') patch({ candidate: c.id, page: String(page) })
              }}
            >
              <Td>
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  {c.incumbent && (
                    <Star className="h-3 w-3 text-chocolate shrink-0" aria-label="Incumbent" />
                  )}
                  <span className="font-medium truncate">{c.fullName}</span>
                </span>
              </Td>
              <Td className="whitespace-nowrap">{seatTypeLabel(c.seatTypeCode)}</Td>
              <Td>
                <span className="block truncate">{c.unitName ?? '—'}</span>
                {c.unitLabels.length > 1 && (
                  <span className="block text-erp-xs text-muted-foreground truncate">
                    {c.unitLabels.slice(0, -1).join(' › ')}
                  </span>
                )}
              </Td>
              <Td>
                {c.party
                  ? <EntityChip entity={{
                      code: c.party.code, name: c.party.name,
                      shortName: c.party.shortName, colourHex: c.party.colourHex,
                    }} />
                  : <span className="text-erp-xs text-muted-foreground">Independent</span>}
              </Td>
              <Td className="axis-hide-sm">
                {c.coalition
                  ? (
                    <EntityChip
                      entity={c.coalition}
                      // Two different facts, so two different chips: inherited
                      // from the party, or held directly.
                      sub={c.coalitionDerived ? 'via party' : 'direct'}
                    />
                  )
                  : <span className="text-erp-xs text-muted-foreground">—</span>}
              </Td>
              <Td>
                <Pill tone={candidateStatusTone(c.statusCode)}>
                  {candidateStatusLabel(c.statusCode)}
                </Pill>
              </Td>
            </tr>
          ))}
        </ErpTable>

        <OffsetPager
          page={page}
          totalPages={list.data?.totalPages ?? 1}
          totalElements={list.data?.totalElements ?? 0}
          onPage={(next) => patch({ page: String(next) })}
          loading={list.loading}
          unit="candidates"
        />
      </Panel>

      {/*
        * KEYED BY THE ROW, so React remounts it instead of reusing it.
        *
        * A drawer that stays mounted across rows keeps every piece of local
        * state from the last one: a half-open form, a typed value, and — worst —
        * the previous row's error. Opening party B after a refusal on party A
        * showed A's refusal under B's name, and the "Join" button toggled a form
        * that was already open from A, so it appeared to do nothing. One line
        * here is cheaper and more reliable than an effect that resets six
        * useStates and has to be extended every time a seventh is added.
        */}
      <CandidateDrawer key={openId ?? 'none'}
        candidateId={openId}
        onClose={() => patch({ candidate: '', page: String(page) })}
        onChanged={list.reload}
        onDeleted={() => { patch({ candidate: '', page: String(page) }); list.reload() }}
      />

      {/* Mounted only while open: the dialog loads seat types, parties and
          coalitions of its own, and a closed dialog must not pay for them. */}
      {creating && (
        <CandidateFormDialog
          open={creating}
          candidate={null}
          onClose={() => setCreating(false)}
          onSaved={(saved) => { setCreating(false); list.reload(); patch({ candidate: saved.id }) }}
        />
      )}
    </div>
  )
}
